export const allCaseStudies = [
  {
    id: 'hands-on-science-meets-literacy-goals',
    slug: 'hands-on-science-meets-literacy-goals',
    title: 'Hands-on science meets literacy goals',
    school: 'North Marion Middle School',
    location: 'Aurora',
    state: 'OR',
    students: '381',
    setting: 'Rural',
    englishLanguageLearners: '43%',
    youtube: 'https://www.youtube.com/watch?v=bBU6dKnL1oo',
    description:
      "See how Stile's team worked closely with teachers to use Stile's feedback tools and lab kits to meet a diverse student population.",
    image: 'stile-shared/assets/images/case-studies/hands-on-science.jpg',
    featureQuote: {
      quote:
        "The combination of the quality of the curriculum and the support from the team is unlike any other curriculum that I've worked another here with.",
      author: 'Chelsea',
      position: 'Instructional Coach',
    },
    fullCaseStudy: `
      <p>
      At North Marion—a rural, AVID-designated middle school with a <mark>high population of Spanish-speaking students students and historically low test scores</mark>—teachers needed a science curriculum that could do more. They found it in Stile.
      </p>
      <p>
      With varied levels of science teaching experience on staff, school leadership leaned on Stile for lessons that were <mark>rigorous, flexible, and deeply engaging.</mark> Instructional coach Chelsea Landry noted that literacy is a major schoolwide focus, and Stile’s integration of reading, writing, and vocabulary into hands-on science was immediately impactful in the classroom. The glossary built into every lesson, for example, allows students to hover over a key term they don’t know to build understanding in English or Spanish. Teachers leaned into the real-time response tools so that students could get timely feedback.
      </p>
      <p>
      For 8th grade teacher Dave Paumier, the improvement has been noticeable, reshaping how his students think and talk about science. He frequently uses Stile’s Analyze Mode at the start of class to identify misconceptions and spark discussion, helping students develop more precise, thoughtful answers.
      </p>
      <p>
      “I like that the brainstorm tool is instantaneous,” Paumier said. “It puts all of the kids’ views up there so they can see what others are thinking.”
      </p>
      <p>
      Tools aside, perhaps the biggest impact of Stile in North Marion classrooms is the opportunity for <mark>digital-free learning.</mark> Stile’s all-in-one lab kits have helped shift student engagement.
      </p>
      <p>
      “Students love the labs—they’re really engaging and fun,” Chelsea said. “There’s just a lot of versatility in what teachers can do with them, which makes for a really powerful learning experience.”
      </p>
      <p>
      Stile’s team prides itself on always being an email, phone call, or text away—and that’s certainly been the case at North Marion. Chelsea has reached out for help in the middle of class when a teacher was having trouble with a lesson or rostering and the response, she noted, has always been immediate.
      </p>
      <p>
      <mark>“We feel very known by the Stile team,</mark> and I can’t say that about any other curriculum,” she said. “From the adoption process to ongoing support, they are hands-on and responsive.”
      </p>
    `,
  },
  {
    id: 'more-time-for-teaching',
    slug: 'more-time-for-teaching',
    title: 'More time for teaching',
    school: 'Haywood County School District',
    location: 'Waynesville',
    state: 'NC',
    students: '1450',
    setting: 'Town',
    englishLanguageLearners: '3%',
    youtube: 'https://www.youtube.com/watch?v=sJ3Xnio77uk',
    description:
      'Teachers shaved hours off of time normally spent lesson planning with Stile, meaning they could spend more time working with students and providing feedback.',
    image: 'stile-shared/assets/images/case-studies/more-time-for-teaching.jpg',

    featureQuote: {
      quote:
        "It's intuitive, it's easy to use. And at the same time it provides a good amount of rigor for our students.",
      author: 'Casey',
      position: 'Principal, Canton Middle School',
    },
    fullCaseStudy: `
      <p>
      When Haywood County Schools began piloting Stile in early 2025, teachers at Canton Middle faced a familiar challenge: <mark>new state standards, limited prep time, and the pressure to engage every learner</mark>. For Karma Schuford, a veteran teacher with 30 years in front of a classroom but minimal experience teaching science —she wasn’t sure what to expect with a new curriculum.      </p>
      <p>
      She was delighted to find that the ready-to-teach lessons shaved hours off her lesson planning time each week. Even better, her students instantly gravitated to the narrative-driven lessons, varying question formats, engaging videos, and labs.
      </p>
      <p>
      “Probably my favorite part about Stile is the ease that I'm able to present to the students and the engagement level they have,” Karma said. “They have bought into the system. <mark>They're very excited about it.</mark> They'll walk in and say, ‘hey, are we doing Stile today?’
      </p>
      <p>
      She’s also found that she’s able to pull more out of her students using feedback tools like the model answer, which can be released by a teacher in real-time to show students what a response should look like. Now, students are trying to anticipate what elements the model answer will include.
      </p>
      <p>
      “They are trying to out-model the model answer, which is great because then they're given more complete, deeper thoughts.”
      </p>
      <p>
      Across the district, <mark>Stile’s customization has been a benefit</mark> for veteran and new teachers alike. Over at Bethel Middle, Dylan Sharp is quite the opposite of Karma — he has a background in science but is relatively new to teaching.
      </p>
      <p>
      “You're given the standards and told, ‘Teach,’” Dylan said. “You’re not handed a textbook or materials, you’re expected to build it all yourself while managing everything else.”
      </p>
      <p>
      His experience with lesson planning has been nearly identical. The alignment to North Carolina’s new science standards meant that Dylan could shift his focus from building materials to building momentum in the classroom. 
      </p>
      <p>
      <mark>“It’s decreased my planning time by 75 to 80 percent,”</mark> he said. “Now I can focus on teaching, not making slideshows.”
      </p>
      <p>
      That shift has translated directly into classroom gains. Real-time tools like Analyze Mode have helped Dylan and his fellow teachers spark meaningful discussion, catch misconceptions early, and give students the feedback they need to grow. Principal Casey Kruk has seen firsthand the ease with which teachers have adapted to Stile throughout the pilot, and the response of the students at his school.
      </p>
      <p>
      <mark>“It's intuitive, it's easy to use,”</mark> Casey said. “And at the same time it provides a good amount of rigor for our students. I think it's easy for me to go in and log in with a teacher and be able to see exactly what should be done on that day, how they interpret that information, how they use it, and how they implement that in a classroom.”
      </p>
    `,
  },
  {
    id: 'engagement-for-everybody',
    slug: 'engagement-for-everybody',
    title: 'Engagement for everybody',
    school: 'Ingenium Charter Middle School',
    location: 'Winnetka',
    state: 'CA',
    students: '232',
    setting: 'Urban',
    englishLanguageLearners: 'High',
    description:
      'Enhancing interaction, confidence, and language skills with differentiated lessons.',
    image: 'stile-shared/assets/images/case-studies/engagement.jpg',

    youtube: 'https://www.youtube.com/watch?v=V0aDHtUdwyk',
    featureQuote: {
      quote:
        'Stile has helped me support ELL students by allowing me to narrate in their language and provide extra help for struggling readers.',
      author: 'Jovanna',
      position: '6th Grade Teacher',
    },
    fullCaseStudy: `
      <p>
      At Ingenium Charter Middle School in Winnetka, California, educators are dedicated to creating an <mark>inclusive and engaging learning environment</mark> for all students. With a diverse student body, including a significant number of English Language Learners, teachers sought a science curriculum that could meet varied learning needs and promote active participation.
      </p>
      <p>
      Stile provided the solution. Its built-in supports, such as vocabulary scaffolding, interactive question types, and audio options in both English and Spanish, empower students to build confidence, articulate their understanding, and engage deeply with scientific concepts.
      </p>
      <p>
      "Stile has helped me support ELL students by <mark>allowing me to narrate in their language</mark> and provide extra help for struggling readers," said Jovana, a 6th-grade teacher at Ingenium Charter Middle School.
      </p>
      <p>
      The curriculum's real-world science issues and intuitive editing tools make it easy for teachers to personalize content, connecting lessons to students' interests and experiences. Jovana's students were particularly engaged when exploring Newton’s Laws through a skateboarding simulation in Stile.
      </p>
      <p>
      "Compared to what I used in the past, <mark>Stile is much more engaging for students,</mark>” she noted. “The simulations allow them to make real-world connections."
      </p>
      <p>
      Stile's flexibility also enables teachers to differentiate instruction effectively, catering to students with varying capabilities.
      </p>
      <p>
      "My students range in their capabilities—some are really behind, and some are advanced,” Jovana explained. “Now, <mark>I can differentiate to meet all their needs.</mark>"
      </p>
      <p>
      By integrating Stile into their classrooms, teachers at Ingenium Charter Middle School are not only enhancing scientific literacy but also fostering an environment where every student feels seen, supported, and inspired to learn.
      </p>
    `,
  },
  {
    id: 'bringing-local-phenomena-into-the-classroom',
    slug: 'bringing-local-phenomena-into-the-classroom',
    title: 'Bringing local phenomena into the classroom',
    school: 'Half Moon Bay High School',
    location: 'Half Moon Bay',
    state: 'CA',
    students: '987',
    setting: 'Rural-suburban',
    englishLanguageLearners: '9%',
    description:
      'See how integration of real-world coastal phenomena helps keep students engaged through exploration of science in their own backyard.',
    image: 'stile-shared/assets/images/case-studies/local-phenomena.jpg',

    youtube: 'https://www.youtube.com/watch?v=LDiF0TwGb2M',
    featureQuote: {
      quote:
        'One of my students almost never speaks up. But once her written answer appeared anonymously on the screen, she actually raised her hand to share it out loud. That shift in confidence is huge.',
      author: 'Joseph',
      position: 'Science Teacher',
    },
    fullCaseStudy: `
      <p>
      For Joseph Centoni, science class isn’t about delivering content—it’s about designing experiences. As an environmental science teacher at Half Moon Bay High School, he builds lessons that are <mark>hands-on, locally relevant, and accessible to every learner</mark>. With Stile, he’s found a flexible platform that brings that creative vision to life.
      </p>
      <p>
      “What I love about teaching is the creativity—designing lessons and coming up with exciting activities that connect to our local environment or to what students care about,” Joseph said.
      </p>
      <p>
      Stile’s built-in curriculum gives him a strong foundation, while the editing tools make it easy to adapt, customize, or combine lessons to suit different student needs. For AP classes, he can deepen the rigor. For students who need more support, he can simplify, scaffold, or break lessons into smaller chunks.
      </p>
      <p>
      <mark>“There’s a great curriculum there, but also the flexibility to make it my own,”</mark> he said. “I can swap in videos, tweak the questions, and tailor it to my students in real time.”
      </p>
      <p>
      That flexibility translates directly into student engagement. Joseph uses Stile’s Teach Mode to launch discussions, surface misconceptions, and spark student thinking—with less reliance on traditional lecture slides. Real-time feedback tools like live polls, anonymous responses, and model answers give every student a chance to participate, whether or not they raise their hand.
      </p>
      <p>
      “It’s changed the way I lecture,” he said. “Students are <mark>actually interacting with the material,</mark> not just sitting and taking notes. Even quieter students are joining the conversation.”
      </p>
      <p>
      The platform’s design also supports diverse learners, offering videos, visuals, and audio narration that make science more accessible—and more memorable.
      </p>
      <p>
      “One of my students almost never speaks up,” Joseph said. “But once her written answer appeared anonymously on the screen, she actually raised her hand to share it out loud. <mark>That shift in confidence is huge.</mark>”
      </p>
      <p>For Joseph, Stile isn’t just about saving time—it’s about making science meaningful.</p>
    `,
  },
  {
    id: 'a-perfect-happy-medium',
    slug: 'a-perfect-happy-medium',
    title: 'A perfect happy medium',
    school: 'Siuslaw Middle School',
    location: 'Florence',
    state: 'OR',
    students: '270',
    setting: 'Town',
    englishLanguageLearners: 'Very low',
    description:
      'McKenzie Perry leverages Stile’s easy-to-use customization tools to adapt lessons to her students’ needs.',
    image: 'stile-shared/assets/images/case-studies/happy-medium.jpg',

    youtube: 'https://youtu.be/aFhEZD6DKZ8',

    featureQuote: {
      quote:
        'I don’t have to change things. I don’t have to make it engaging—because it’s already engaging. But if I’ve got videos I love, I can add those in too. It’s the perfect happy medium.',
      author: 'McKenzie',
      position: 'Science Teacher',
    },
    fullCaseStudy: `
      <p>
      Curriculum adoption used to be something that McKenzie Perry dreaded. After years of wrestling with rigid materials that demanded constant editing, the Siuslaw middle school science teacher was ready for something different. <mark>When she sat in on a Stile session, something clicked.</mark>
      </p>
      <p>
      “The main thing for me was that it was editable—but I didn’t have to edit it every day,” McKenzie said. <mark>“It was a game changer.”</mark>
      </p>
      <p>
      Now, she spends less time searching for materials or trying to make lessons engaging. Stile’s interactive content, built-in videos, and easy customization tools give her the best of both worlds: a curriculum that works out of the box, and the flexibility to adapt when needed.
      </p>
      <p>
      “I don’t have to change things. I don’t have to make it engaging—because it’s already engaging,” she said. “But if I’ve got videos I love, I can add those in too. <mark>It’s the perfect happy medium.”</mark>
      </p>
    `,
  },
  {
    id: 'ready-made-curriculum-thats-flexibly-customizable',
    slug: 'ready-made-curriculum-thats-flexibly-customizable',
    title: 'Ready-made curriculum that’s flexibly customizable',
    school: 'High Tech LA Middle School',
    location: 'Los Angeles',
    state: 'CA',
    students: '244',
    setting: 'Urban',
    englishLanguageLearners: 'Low',
    description:
      'Using Stile X workbooks is helping students at different lexile levels build confidence, work independently, and stay more engaged in class.',
    image: 'stile-shared/assets/images/case-studies/ready-made-curriculum.jpg',

    featureQuote: {
      quote:
        'I’m seeing more complete thoughts—more intended thinking in their answers, even from students who used to write just one line.',
      author: 'Melissa',
      position: 'Science Teacher',
    },
    fullCaseStudy: `
      <p>
      For Melissa Thorpe, Stile X isn’t just a review tool—it’s a bridge between thinking and writing, between digital learning and deeper understanding.
      </p>
      <p>
      Since incorporating Stile X alongside Stile Digital, Melissa has seen her sixth graders at High Tech LA become <mark>more engaged, more independent, and more confident</mark> in their science thinking.
      </p>
      <p>
      <mark>“You need Stile Digital and Stile X together,”</mark> she said. “My students need to be able to physically write and draw things to learn best.”
      </p>
      <p>
      Melissa uses Stile X notebooks in a variety of ways: as collaborative jigsaw reviews, do-now activities to revisit key concepts, and in-lesson scaffolding for vocabulary and note-taking. She’s noticed that students are more likely to recall information, write in full sentences, and show clear, organized thinking—without needing to be prompted.
      </p>
      <p>
      “I’m seeing more complete thoughts,” she said. “More intended thinking in their answers, even from students who used to write just one line.”
      </p>
      <p>
      <mark>That impact is especially clear with students who need extra support.</mark> For students with IEPs or ADHD, the clear layout and chunked structure of Stile X has reduced stress and helped them retain and apply information. Melissa even allows some students to use their notebooks during quizzes—because, as she puts it, “most of us as adults get to reference what we need to do our work. This is no different.”
      </p>
      <p>
      Her students agree. They’ve personalized their notebooks, enjoy the routine of using them, and appreciate having a resource that follows the digital lessons so closely.
      </p>
      <p>
      “They like that it’s easy to find what they need,” she said. “And they really like the structure—<mark>it helps them feel successful.</mark>”
      </p>
    `,
  },
  {
    id: 'collaboration-tools-that-make-thinking-visible',
    slug: 'collaboration-tools-that-make-thinking-visible',
    title: 'Collaboration tools that make thinking visible',
    school: 'St Agatha School',
    location: 'Portland',
    state: 'OR',
    students: '50–60',
    setting: 'Urban',
    englishLanguageLearners: 'Minimal',
    description:
      "Learn about the Stile tools helping Abby's students get one step closer to mastery.",
    image: 'stile-shared/assets/images/case-studies/collaboration-tools.jpg',
    mp4: 'stile-shared/assets/videos/case-studies/st_agatha_case_study.mp4',
    featureQuote: {
      quote:
        'I like that the brainstorm tool is instantaneous. It puts all of the kids’ views up there, so they can see what others are thinking.',
      author: 'Abby',
      position: 'Science Teacher',
    },
    fullCaseStudy: `
      <p>
      Abby Richardson teaches science and technology to grades 6 through 8 at St. Agatha School. Before Stile, she often found herself stitching together lessons that didn’t quite fit. Since adopting Stile in 2022, that’s changed.
      </p>
      <p>
      <mark>“Stile has made planning a lot easier,”</mark> she said. “There’s a lot less work on my part. <mark>I trust the lessons,</mark> I know the style, and if I don’t have time to fully proof the lesson beforehand, I know it’ll still be good. And I can still add my own resources. <mark>It’s adaptable.</mark>”
      </p>
      <p>
      Stile’s structured scope and sequence makes day-to-day planning easier and gives Abby the space to focus on what matters most: her students.
      </p>
      <p>
      She’s also found that Stile’s interactive features <mark>help students stay focused and excited to learn.</mark> From drag-and-drop activities to polls, audio uploads, and written response questions, every student can engage in ways that match their learning style.
      </p>
      <p>
      “I like that the brainstorm tool is instantaneous,” Abby said. “It puts all of the kids’ views up there, so they can see what others are thinking.”
      </p>
      <p>
      For the first time in a decade, Abby felt confident running a science fair—with Stile’s Student Research Project template helping students build their Science and Engineering Practices.
      </p>
      <p>
      And with in-lesson feedback tools, she’s able to leave targeted comments on student work and revisit those ideas in class. “Students can take action on the feedback for homework or the next day,” she said. <mark>“They’re reaching mastery—and they like that.”</mark>
      </p>
    `,
  },
];
